import React, { useEffect, useRef, useState } from "react";
import {
  Send,
  X,
  Paperclip,
  FileText,
  Sliders,
  Download,
  ShieldAlert,
  Database,
  Globe,
  Sparkles,
  Zap,
  Settings2,
  Upload,
  FileSpreadsheet,
  Image as ImageIcon,
  FileCode,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { AppMode, AttachedFile } from "../types";
import * as mammoth from "mammoth";
import * as XLSX from "xlsx";

interface InputEngineProps {
  onSendMessage: (content: string, attachments?: AttachedFile[]) => void | Promise<void>;
  currentMode: AppMode;
  activeContextCount: number;

  searchGroundingEnabled: boolean;
  onToggleSearchGrounding: () => void;

  imageGenerationEnabled: boolean;
  onToggleImageGeneration: () => void;

  driveSyncEnabled: boolean;
  onToggleDriveSync: () => void;

  fastModeEnabled: boolean;
  onToggleFastMode: () => void;

  aiTemperature: number;
  onTemperatureChange: (temp: number) => void;

  accuracyMode: "strict" | "balanced" | "creative";
  onAccuracyModeChange: (val: "strict" | "balanced" | "creative") => void;

  responseLength: "tight" | "short" | "normal" | "long" | "full" | "quick" | "detailed";
  onResponseLengthChange: (val: "tight" | "short" | "normal" | "long" | "full" | "quick" | "detailed") => void;

  jobSensitivity: "routine" | "important" | "critical";
  onJobSensitivityChange: (val: "routine" | "important" | "critical") => void;

  aiPreset: string;
  onSelectPreset: (preset: string) => void;

  onOpenExporter?: () => void;
  globalTheme?: "rodgerspc" | "light" | "dark";
  chatTheme?: "light" | "dark";
  onUploadFile?: (name: string, content: string, mimeType?: string) => void | Promise<void>;
}

type AttachmentKind = "image" | "pdf" | "document" | "spreadsheet" | "text" | "binary";

const MAX_ATTACHMENTS_PER_MESSAGE = 20;
const MAX_TEXT_ATTACHMENT_CHARS = 250000;
const MAX_IMAGE_EDGE_PX = 2200;
const MAX_IMAGE_DATA_URL_CHARS = 9500000;
const JPEG_FALLBACK_QUALITY = 0.88;


function isFakeOrOptimizedPayload(value: unknown): boolean {
  const raw = String(value || "");
  if (!raw.trim()) return false;
  const lower = raw.toLowerCase();
  return (
    lower.includes("[payload size optimized]") ||
    lower.includes("payload size optimized") ||
    lower.includes("[base64 omitted]") ||
    lower.includes("base64 omitted") ||
    lower.includes("visual payload omitted")
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error(`Failed to read ${file.name}`));
    reader.onload = (event) => resolve((event.target?.result as string) || "");
    reader.readAsDataURL(file);
  });
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image could not be decoded for request-size optimization."));
    img.src = dataUrl;
  });
}

async function optimizeImageFileForGemini(file: File): Promise<{ dataUrl: string; mimeType: string; optimized: boolean; note: string }> {
  const originalDataUrl = await readFileAsDataUrl(file);

  if (!originalDataUrl || isFakeOrOptimizedPayload(originalDataUrl)) {
    throw new Error("Image payload was empty or already replaced by a placeholder.");
  }

  // Keep already-small images untouched. This preserves sharp screenshot text when payload size is safe.
  if (originalDataUrl.length <= MAX_IMAGE_DATA_URL_CHARS && file.size <= 7 * 1024 * 1024) {
    return {
      dataUrl: originalDataUrl,
      mimeType: file.type || originalDataUrl.match(/^data:([^;]+);/)?.[1] || "image/png",
      optimized: false,
      note: "original image payload retained",
    };
  }

  const image = await loadImageFromDataUrl(originalDataUrl);
  const sourceWidth = image.naturalWidth || image.width || 1;
  const sourceHeight = image.naturalHeight || image.height || 1;
  const longestEdge = Math.max(sourceWidth, sourceHeight);
  const scale = longestEdge > MAX_IMAGE_EDGE_PX ? MAX_IMAGE_EDGE_PX / longestEdge : 1;
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return {
      dataUrl: originalDataUrl,
      mimeType: file.type || "image/png",
      optimized: false,
      note: "canvas unavailable; original image payload retained",
    };
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  let optimizedDataUrl = canvas.toDataURL("image/png");
  let outputMime = "image/png";

  // Large phone screenshots/photos can remain huge as PNG. Use JPEG fallback only when necessary.
  if (optimizedDataUrl.length > MAX_IMAGE_DATA_URL_CHARS) {
    optimizedDataUrl = canvas.toDataURL("image/jpeg", JPEG_FALLBACK_QUALITY);
    outputMime = "image/jpeg";
  }

  if (!optimizedDataUrl || isFakeOrOptimizedPayload(optimizedDataUrl)) {
    throw new Error("Image optimization produced an invalid payload.");
  }

  return {
    dataUrl: optimizedDataUrl,
    mimeType: outputMime,
    optimized: optimizedDataUrl !== originalDataUrl,
    note: optimizedDataUrl !== originalDataUrl
      ? `optimized from ${sourceWidth}x${sourceHeight} to ${targetWidth}x${targetHeight}`
      : "original image payload retained",
  };
}

function truncateAttachmentText(text: string, maxChars = MAX_TEXT_ATTACHMENT_CHARS): string {
  if (!text) return "";
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}\n\n[Attachment text truncated automatically to protect request size.]`;
}

function getModeLabel(mode: AppMode): string {
  switch (mode) {
    case AppMode.TECH_ASSISTANT:
      return "Bench Tech";
    case AppMode.BLOG_ENGINE:
      return "Blog Engine";
    case AppMode.PERSONAL_VAULT:
      return "Personal Vault";
    case AppMode.INVENTORY_SCOUT:
      return "Inventory Scout";
    case AppMode.LEAD_RESPONDER:
      return "Lead Responder";
    case AppMode.SEO_LISTINGS:
      return "SEO & Listings";
    case AppMode.WEB_CREATIVE:
      return "Web Creative";
    case AppMode.FRONT_DESK:
      return "Front Desk";
    default:
      return String(mode || "")
        .replace(/^RodgersPC\s*/i, "")
        .replace(/_/g, " ");
  }
}

function getAttachmentKind(file: File): AttachmentKind {
  const name = file.name.toLowerCase();

  if (file.type.startsWith("image/")) return "image";
  if (file.type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (/\.(docx|doc)$/i.test(name)) return "document";
  if (/\.(xlsx|xls)$/i.test(name)) return "spreadsheet";

  if (
    file.type.startsWith("text/") ||
    file.type.includes("json") ||
    file.type.includes("xml") ||
    /\.(txt|md|json|csv|log|html|htm|css|js|jsx|ts|tsx|py|sh|ps1|bat|cmd|reg|ini|yml|yaml)$/i.test(name)
  ) {
    return "text";
  }

  return "binary";
}

function getAttachmentIcon(type: AttachmentKind) {
  if (type === "image") return <ImageIcon className="h-4 w-4" />;
  if (type === "spreadsheet") return <FileSpreadsheet className="h-4 w-4" />;
  if (type === "document" || type === "pdf") return <FileText className="h-4 w-4" />;
  if (type === "text") return <FileCode className="h-4 w-4" />;
  return <Paperclip className="h-4 w-4" />;
}

function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isDestructivePrompt(text: string): boolean {
  const normalized = ` ${text.toLowerCase().replace(/[’‘]/g, "'").replace(/[^a-z0-9_.\/-]+/g, " ").replace(/\s+/g, " ").trim()} `;

  if (!normalized.trim()) return false;

  const destructivePatterns: RegExp[] = [
    /\bdiskpart\s+clean\b/,
    /\bclean\s+all\b/,
    /\bdelete\s+(?:partition|volume|drive|folder|directory|file|files)\b/,
    /\bformat\s+(?:[a-z]:|disk|drive|partition|volume|usb|ssd|hdd|nvme)\b/,
    /\b(?:wipe|erase)\s+(?:drive|disk|ssd|hdd|nvme|usb|partition|volume|data|everything|all)\b/,
    /\bfactory\s+reset\b/,
    /\breset\s+this\s+pc\b/,
    /\breinstall\s+windows\b/,
    /\bclean\s+install\b/,
    /\bremove-item\s+[^\n]*\s-(?:recurse|r)\b/,
    /\brd\s+\/s\b/,
    /\brmdir\s+\/s\b/,
    /\brm\s+[^\n]*\s-rf\b/,
    /\breg\s+delete\b/,
    /\bbcdedit\b/,
  ];

  return destructivePatterns.some((pattern) => pattern.test(normalized));
}

function getMimeForDriveUpload(att: AttachedFile): string {
  const type = String(att.type || "").toLowerCase();

  if (type === "image" && att.content.startsWith("data:")) {
    const match = att.content.match(/^data:([^;]+);/);
    return match?.[1] || "image/png";
  }

  if (type === "pdf") return "application/pdf";
  if (type === "spreadsheet") return "text/csv";
  if (type === "document") return "text/plain";
  if (type === "text") return "text/plain";

  return "application/octet-stream";
}

function presetLabel(aiPreset: string): string {
  if (aiPreset === "bench_job") return "Bench";
  if (aiPreset === "front_counter") return "Front";
  if (aiPreset === "creative_seo") return "SEO";
  return "Custom";
}

function outputLengthLabel(value: string): string {
  const normalized = String(value || "").toLowerCase();

  if (normalized === "tight") return "Tight";
  if (normalized === "short" || normalized === "quick") return "Short";
  if (normalized === "normal" || normalized === "medium") return "Normal";
  if (normalized === "long") return "Long";
  if (normalized === "full" || normalized === "detailed" || normalized === "high") return "Full";

  return "Short";
}


function buildPlaceholder(mode: AppMode): string {
  const label = getModeLabel(mode);
  return `Ask ${label}. Attach screenshots/logs/files; current-turn visuals are sent as real evidence.`;
}

export default function InputEngine({
  onSendMessage,
  currentMode,
  activeContextCount,
  searchGroundingEnabled,
  onToggleSearchGrounding,
  imageGenerationEnabled,
  onToggleImageGeneration,
  driveSyncEnabled,
  onToggleDriveSync,
  fastModeEnabled,
  onToggleFastMode,
  aiTemperature,
  onTemperatureChange,
  accuracyMode,
  onAccuracyModeChange,
  responseLength,
  onResponseLengthChange,
  jobSensitivity,
  onJobSensitivityChange,
  aiPreset,
  onSelectPreset,
  onOpenExporter,
  globalTheme = "rodgerspc",
  chatTheme = "dark",
  onUploadFile,
}: InputEngineProps) {
  const [inputText, setInputText] = useState("");
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);
  const [parseLoading, setParseLoading] = useState(false);
  const [dragOverTextarea, setDragOverTextarea] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [driveUploadBusy, setDriveUploadBusy] = useState(false);
  const [driveUploadStatus, setDriveUploadStatus] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settingsDropdownRef = useRef<HTMLDivElement>(null);

  const inputTheme: "rodgerspc" | "light" | "dark" =
    chatTheme === "light" ? "light" : globalTheme === "dark" ? "dark" : "rodgerspc";

  const isLight = inputTheme === "light";
  const isDark = inputTheme === "dark";
  const rejectedAttachmentCount = attachments.filter((att) => att.payloadRejected || isFakeOrOptimizedPayload(att.content) || isFakeOrOptimizedPayload(att.base64)).length;
  const hasPayload = inputText.trim().length > 0 || attachments.length > 0;
  const destructivePrompt = isDestructivePrompt(inputText);
  const canSend = hasPayload && !parseLoading && rejectedAttachmentCount === 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setShowSettings(false);
    }

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showSettings]);

  useEffect(() => {
    if (!textareaRef.current) return;

    textareaRef.current.style.height = "auto";
    const scrollHeight = textareaRef.current.scrollHeight;
    const newHeight = Math.max(74, Math.min(scrollHeight, 260));
    textareaRef.current.style.height = `${newHeight}px`;
  }, [inputText]);

  const focusTextareaSoon = () => {
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus({ preventScroll: true });
    });
  };

  const handleSend = async () => {
    if (!canSend) return;

    const outgoingText = inputText.trim();
    const outgoingAttachments = [...attachments];

    setInputText("");
    setAttachments([]);
    setParseError(null);
    setDriveUploadStatus(null);
    focusTextareaSoon();

    try {
      await Promise.resolve(onSendMessage(outgoingText, outgoingAttachments));
    } finally {
      focusTextareaSoon();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const readFileToAttachment = async (file: File): Promise<AttachedFile | null> => {
    const type = getAttachmentKind(file);

    try {
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(reader.error || new Error(`Failed to read ${file.name}`));

        if (type === "image") {
          optimizeImageFileForGemini(file)
            .then((result) => resolve(result.dataUrl))
            .catch(reject);
          return;
        }

        if (type === "pdf" || type === "binary") {
          reader.onload = (event) => resolve((event.target?.result as string) || "");
          reader.readAsDataURL(file);
          return;
        }

        if (type === "document") {
          reader.onload = async (event) => {
            try {
              const arrayBuffer = event.target?.result as ArrayBuffer;
              const result = await mammoth.extractRawText({ arrayBuffer });
              resolve(truncateAttachmentText(result.value || "[Empty Word document]"));
            } catch (err) {
              reject(err);
            }
          };
          reader.readAsArrayBuffer(file);
          return;
        }

        if (type === "spreadsheet") {
          reader.onload = (event) => {
            try {
              const arrayBuffer = event.target?.result as ArrayBuffer;
              const data = new Uint8Array(arrayBuffer);
              const workbook = XLSX.read(data, { type: "array" });

              let textContent = "";

              workbook.SheetNames.forEach((sheetName) => {
                const worksheet = workbook.Sheets[sheetName];
                const csv = XLSX.utils.sheet_to_csv(worksheet);

                if (csv.trim()) {
                  textContent += `--- Sheet: ${sheetName} ---\n${csv}\n\n`;
                }
              });

              resolve(truncateAttachmentText(textContent || "[Empty spreadsheet]"));
            } catch (err) {
              reject(err);
            }
          };
          reader.readAsArrayBuffer(file);
          return;
        }

        reader.onload = (event) => resolve(truncateAttachmentText((event.target?.result as string) || ""));
        reader.readAsText(file);
      });

      const payloadRejected = isFakeOrOptimizedPayload(fileContent);
      const parsedMimeType = fileContent.match(/^data:([^;]+);base64,/)?.[1];
      const isInlineBinary = type === "image" || type === "pdf" || type === "binary";

      return {
        id: `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        type: type === "binary" ? "binary" : type,
        content: fileContent,
        base64: isInlineBinary && !payloadRejected ? fileContent : undefined,
        rawSize: file.size,
        mimeType: parsedMimeType || file.type || undefined,
        source: "upload",
        isRealPayload: !payloadRejected,
        payloadRejected,
        rejectedReason: payloadRejected ? "Attachment payload was a placeholder/truncated marker, not real file data." : undefined,
        visualInspectionState: type === "image" && !payloadRejected ? "sent" : type === "image" ? "not_sent" : undefined,
      };
    } catch (err: any) {
      console.error("Failed to parse attached file:", file.name, err);
      setParseError(`Could not parse ${file.name}: ${err?.message || err}`);
      return null;
    }
  };

  const processFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;

    setParseLoading(true);
    setParseError(null);

    const newAttachments: AttachedFile[] = [];

    try {
      for (const file of list) {
        if (attachments.length + newAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
          setParseError(`Attachment limit reached. Maximum ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
          break;
        }

        const attachment = await readFileToAttachment(file);
        if (attachment) newAttachments.push(attachment);
      }

      if (newAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...newAttachments]);
      }
    } finally {
      setParseLoading(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    if (clipboardData.files && clipboardData.files.length > 0) {
      e.preventDefault();
      processFiles(clipboardData.files);
      return;
    }

    const files: File[] = [];

    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];

      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      processFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverTextarea(true);
  };

  const handleDragLeave = () => {
    setDragOverTextarea(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverTextarea(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleAttachFileBtn = () => {
    fileInputRef.current?.click();
  };

  const handleFileAttached = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleAddAttachmentsToDrive = async () => {
    if (!onUploadFile || attachments.length === 0 || driveUploadBusy) return;

    setDriveUploadBusy(true);
    setDriveUploadStatus(null);
    setParseError(null);

    try {
      for (const att of attachments) {
        const payload = att.base64 || att.content || "";
        if (!payload) {
          throw new Error(`${att.name} has no uploadable payload.`);
        }

        await Promise.resolve(onUploadFile(att.name, payload, getMimeForDriveUpload(att)));
      }

      setDriveUploadStatus(`${attachments.length} attachment${attachments.length === 1 ? "" : "s"} sent to Drive Brain.`);
    } catch (err: any) {
      setParseError(`Drive upload failed: ${err?.message || err}`);
      setDriveUploadStatus(null);
    } finally {
      setDriveUploadBusy(false);
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const shellClass = isLight
    ? "bg-[linear-gradient(180deg,rgba(245,248,252,0.96),rgba(232,238,247,0.94))] border-slate-200 text-slate-950 shadow-[0_-16px_42px_rgba(15,23,42,0.10)]"
    : isDark
      ? "bg-[linear-gradient(180deg,#141923,#0f131b)] border-white/10 text-neutral-100 shadow-[0_-18px_42px_rgba(0,0,0,0.38)]"
      : "bg-[linear-gradient(180deg,#0A2557,#071A3D)] border-[#F6A400]/20 text-white shadow-[0_-18px_42px_rgba(0,0,0,0.32)]";

  const cardClass = isLight
    ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(247,250,253,0.90))] border-slate-300/80 shadow-[0_12px_34px_rgba(15,23,42,0.10),inset_0_1px_0_rgba(255,255,255,0.9)]"
    : isDark
      ? "bg-[linear-gradient(180deg,rgba(25,30,42,0.96),rgba(15,19,27,0.96))] border-white/10 shadow-[0_14px_36px_rgba(0,0,0,0.38),inset_0_1px_0_rgba(255,255,255,0.06)]"
      : "bg-[linear-gradient(180deg,rgba(15,47,105,0.96),rgba(8,31,73,0.96))] border-[#F6A400]/25 shadow-[0_14px_36px_rgba(0,0,0,0.30),inset_0_1px_0_rgba(255,255,255,0.07)]";

  const textMutedClass = isLight ? "text-slate-600" : isDark ? "text-neutral-400" : "text-slate-300";
  const textStrongClass = isLight ? "text-slate-950" : "text-white";
  const accentClass = isLight ? "text-[#143F8F]" : isDark ? "text-[#F6A400]" : "text-[#FFB72B]";
  const inputClass = isLight
    ? "text-slate-950 placeholder:text-slate-500"
    : isDark
      ? "text-neutral-100 placeholder:text-neutral-500"
      : "text-white placeholder:text-white/50";

  const primaryButtonClass = canSend
    ? isLight
      ? "bg-[#143F8F] border-[#143F8F] text-white hover:bg-[#0B2F73]"
      : "bg-[#F6A400] border-[#F6A400] text-[#071A3D] hover:bg-[#FFB72B]"
    : isLight
      ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
      : "bg-white/5 border-white/10 text-white/25 cursor-not-allowed";

  const compactButtonBase = "inline-flex h-10 min-h-10 items-center justify-center gap-2.5 rounded-xl border px-4 text-[12px] font-black tracking-wide transition-all shadow-sm";
  const inactiveCompactClass = isLight
    ? "bg-white border-slate-200 text-slate-700 hover:border-[#143F8F]/25 hover:bg-slate-50 hover:text-[#143F8F]"
    : isDark
      ? "bg-white/[0.045] border-white/10 text-neutral-300 hover:bg-white/[0.075] hover:border-white/18 hover:text-white"
      : "bg-[#0A2557]/95 border-white/10 text-slate-200 hover:bg-[#103B7A] hover:border-[#7EA6E8]/35 hover:text-white";

  const enabledToolClass = isLight
    ? "bg-[#143F8F] border-[#143F8F] text-white shadow-[0_10px_24px_rgba(20,63,143,0.16)]"
    : isDark
      ? "bg-[#1D3557] border-[#7EA6E8]/35 text-[#EAF2FF] shadow-[0_10px_24px_rgba(0,0,0,0.22)]"
      : "bg-[#123F8C] border-[#7EA6E8]/40 text-[#F7FBFF] shadow-[0_10px_24px_rgba(18,63,140,0.22)]";

  return (
    <div className={`shrink-0 border-t p-3 ${shellClass}`} id="prompt-input-engine" data-input-theme={inputTheme}>
      <div
        className={`rpc-input-panel-card mx-auto flex max-w-7xl flex-col overflow-visible rounded-2xl border ${cardClass}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {(attachments.length > 0 || parseLoading || parseError || driveUploadStatus || destructivePrompt) && (
          <div className={`border-b px-4 py-3 ${isLight ? "border-slate-200 bg-slate-50/70" : "border-white/10 bg-black/10"}`}>
            {attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {attachments.map((att) => {
                  const isImg = att.type === "image";
                  const typeLabel = String(att.type || "file").toUpperCase();

                  return (
                    <div
                      key={att.id}
                      className={`group relative flex max-w-xs items-center gap-2 rounded-xl border px-2.5 py-2 text-[10px] ${
                        isLight ? "border-slate-200 bg-white text-slate-700" : "border-white/10 bg-white/[0.04] text-neutral-300"
                      }`}
                    >
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border ${isLight ? "border-slate-200 bg-slate-50" : "border-white/10 bg-black/20"}`}>
                        {isImg && att.content ? (
                          <img src={att.content} alt={att.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <div className={accentClass}>
                            {getAttachmentIcon((att.type as AttachmentKind) || "text")}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 pr-5">
                        <p className="truncate font-bold" title={att.name}>
                          {att.name}
                        </p>
                        <p className={`mt-0.5 text-[8px] uppercase ${textMutedClass}`}>
                          {typeLabel} · {formatBytes(att.rawSize)}{att.type === "image" && att.isRealPayload !== false ? " · visual payload ready" : ""}
                        </p>
                        {att.payloadRejected && (
                          <p className="mt-0.5 text-[8px] font-black uppercase text-rose-500">
                            payload rejected
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeAttachment(att.id)}
                        className="absolute right-1 top-1 rounded-full p-0.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-500"
                        title="Remove attachment"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}

                {onUploadFile && (
                  <button
                    type="button"
                    onClick={handleAddAttachmentsToDrive}
                    disabled={driveUploadBusy || attachments.length === 0}
                    className={`${compactButtonBase} ml-auto ${inactiveCompactClass} disabled:cursor-wait disabled:opacity-60`}
                    title="Upload these attachments into the RodgersPC Drive Brain."
                  >
                    <Database className="h-3.5 w-3.5" />
                    {driveUploadBusy ? "Uploading" : "Upload to Drive"}
                  </button>
                )}
              </div>
            )}

            {parseLoading && (
              <StatusLine tone="warning" isLight={isLight} icon={<span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />}>
                Reading attachments for this message.
              </StatusLine>
            )}

            {parseError && (
              <StatusLine tone="danger" isLight={isLight} icon={<AlertTriangle className="h-3.5 w-3.5" />} onDismiss={() => setParseError(null)}>
                {parseError}
              </StatusLine>
            )}

            {driveUploadStatus && (
              <StatusLine tone="success" isLight={isLight} icon={<CheckCircle2 className="h-3.5 w-3.5" />} onDismiss={() => setDriveUploadStatus(null)}>
                {driveUploadStatus}
              </StatusLine>
            )}

            {rejectedAttachmentCount > 0 && (
              <StatusLine tone="danger" isLight={isLight} icon={<AlertTriangle className="h-3.5 w-3.5" />}>
                One or more attachments contain a fake/truncated visual marker instead of real file data. Remove and reattach the file before sending so Bench Tech does not reason from missing screenshots.
              </StatusLine>
            )}

            {destructivePrompt && (
              <StatusLine tone="warning" isLight={isLight} icon={<ShieldAlert className="h-3.5 w-3.5" />}>
                Destructive action detected. Confirm the target device/path and customer data risk before deletes, disk writes, resets, partition changes, registry edits, or reinstall work.
              </StatusLine>
            )}
          </div>
        )}

        <div className="relative">
          <div className={`flex items-center gap-2 px-4 pt-3 text-[10px] font-mono uppercase tracking-wider ${textMutedClass}`}>
            <span className="font-black">Mode</span>
            <span className={`font-black ${accentClass}`}>{getModeLabel(currentMode)}</span>
            <span>·</span>
            <span>{presetLabel(aiPreset)}</span>
            <span>·</span>
            <span>{accuracyMode}</span>
            <span>·</span>
            <span>{outputLengthLabel(responseLength)}</span>
            <span>·</span>
            <span>{jobSensitivity}</span>
          </div>

          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={buildPlaceholder(currentMode)}
            className={`block w-full resize-none border-0 bg-transparent px-4 py-3 text-[14px] leading-relaxed outline-none focus:outline-none focus:ring-0 ${inputClass} ${
              dragOverTextarea ? "opacity-30" : ""
            }`}
            style={{ minHeight: "74px" }}
            id="chat-textarea-input"
          />

          {dragOverTextarea && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-[#F6A400] bg-[#F6A400]/10">
              <div className={`rounded-xl border px-5 py-3 text-xs font-black uppercase tracking-widest ${isLight ? "border-amber-300 bg-white text-amber-700" : "border-[#F6A400]/40 bg-[#071A3D]/95 text-[#F6A400]"}`}>
                Drop files into this message
              </div>
            </div>
          )}
        </div>

        <div className={`flex flex-col gap-3 border-t px-4 py-3 lg:flex-row lg:items-center lg:justify-between ${isLight ? "border-slate-200 bg-slate-50/55" : "border-white/10 bg-black/10"}`}>
          <div className="flex flex-wrap items-center gap-2">
            <ToolToggle
              label="Drive"
              active={driveSyncEnabled}
              onClick={onToggleDriveSync}
              icon={<Database className="h-4 w-4" />}
              activeText={driveSyncEnabled ? `${activeContextCount} focused` : "off"}
              activeClass={enabledToolClass}
              inactiveClass={inactiveCompactClass}
              baseClass={compactButtonBase}
              title="Turn Drive access on or off."
            />

            <ToolToggle
              label="Web"
              active={searchGroundingEnabled}
              onClick={onToggleSearchGrounding}
              icon={<Globe className="h-4 w-4" />}
              activeText={searchGroundingEnabled ? "on" : "off"}
              activeClass={enabledToolClass}
              inactiveClass={inactiveCompactClass}
              baseClass={compactButtonBase}
              title="Turn live web/search grounding on or off."
            />

            <ToolToggle
              label="Image"
              active={imageGenerationEnabled}
              onClick={onToggleImageGeneration}
              icon={<Sparkles className="h-4 w-4" />}
              activeText={imageGenerationEnabled ? "on" : "off"}
              activeClass={enabledToolClass}
              inactiveClass={inactiveCompactClass}
              baseClass={compactButtonBase}
              title="Turn image generation on or off."
            />

            <button
              type="button"
              onClick={onToggleFastMode}
              className={`${compactButtonBase} ${fastModeEnabled ? enabledToolClass : inactiveCompactClass}`}
              title="Speed profile. Fast prioritizes response speed; Accurate prioritizes stability and careful reasoning."
              id="toggle-fast-mode-btn"
            >
              <Zap className="h-4 w-4" />
              <span>{fastModeEnabled ? "Fast" : "Accurate"}</span>
            </button>

            <div className="relative" ref={settingsDropdownRef}>
              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                className={`${compactButtonBase} ${showSettings ? enabledToolClass : inactiveCompactClass}`}
                title="Adjust accuracy, sensitivity, presets, and advanced temperature. Output length is controlled by the Len button."
                id="ai-control-parameters-btn"
              >
                <Sliders className="h-4 w-4" />
                <span>Controls</span>
              </button>

              {showSettings && (
                <div
                  className={`absolute bottom-full left-0 z-50 mb-3 w-[340px] rounded-2xl border p-4 shadow-2xl ${
                    isLight
                      ? "border-slate-200 bg-white text-slate-900 shadow-slate-300/70"
                      : isDark
                        ? "border-white/10 bg-[#11141D] text-neutral-100 shadow-black"
                        : "border-[#F6A400]/30 bg-[#071A3D] text-white shadow-black"
                  }`}
                >
                  <div className={`mb-3 flex items-center justify-between border-b pb-3 ${isLight ? "border-slate-200" : "border-white/10"}`}>
                    <div>
                      <div className={`text-[10px] font-black uppercase tracking-widest ${accentClass}`}>AI Controls</div>
                      <div className={`mt-1 text-[11px] ${textMutedClass}`}>Plain-English behavior settings. Length is controlled by the Len button.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSettings(false)}
                      className={`rounded-lg p-1.5 ${isLight ? "text-slate-400 hover:bg-slate-100 hover:text-slate-700" : "text-neutral-500 hover:bg-white/10 hover:text-white"}`}
                      aria-label="Close AI controls"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <SegmentedControl
                      label="Preset"
                      value={aiPreset}
                      onChange={onSelectPreset}
                      options={[
                        { value: "bench_job", label: "Bench" },
                        { value: "front_counter", label: "Front" },
                        { value: "creative_seo", label: "SEO" },
                      ]}
                      isLight={isLight}
                    />

                    <SelectControl
                      label="Accuracy"
                      value={accuracyMode}
                      onChange={(val) => onAccuracyModeChange(val as "strict" | "balanced" | "creative")}
                      options={[
                        { value: "strict", label: "Strict — no guessing" },
                        { value: "balanced", label: "Balanced — useful, labeled uncertainty" },
                        { value: "creative", label: "Creative — ideas, still honest" },
                      ]}
                      isLight={isLight}
                    />

                    <SelectControl
                      label="Sensitivity"
                      value={jobSensitivity}
                      onChange={(val) => onJobSensitivityChange(val as "routine" | "important" | "critical")}
                      options={[
                        { value: "routine", label: "Routine" },
                        { value: "important", label: "Important" },
                        { value: "critical", label: "Critical — data/billing/destructive risk" },
                      ]}
                      isLight={isLight}
                    />

                    <div className={`rounded-xl border p-3 ${isLight ? "border-slate-200 bg-slate-50" : "border-white/10 bg-black/10"}`}>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className={`flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wide ${accentClass}`}
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                        {showAdvanced ? "Hide advanced" : "Show advanced"}
                      </button>

                      {showAdvanced && (
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className={textMutedClass}>Temperature</span>
                            <span className={`font-mono font-black ${accentClass}`}>{aiTemperature.toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.0"
                            max="1.0"
                            step="0.05"
                            value={aiTemperature}
                            onChange={(e) => onTemperatureChange(Number.parseFloat(e.target.value))}
                            className="w-full cursor-pointer"
                          />
                          <div className={`flex justify-between text-[9px] ${textMutedClass}`}>
                            <span>0.0 factual</span>
                            <span>1.0 creative</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {onOpenExporter && (
              <button
                type="button"
                onClick={onOpenExporter}
                className={`${compactButtonBase} ${inactiveCompactClass}`}
                title="Export chat/logs."
                id="ai-exporter-bottom-btn"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleAttachFileBtn}
              className={`${compactButtonBase} ${inactiveCompactClass}`}
              title="Attach screenshots, logs, docs, spreadsheets, PDFs, or code."
            >
              <Upload className="h-4 w-4" />
              <span>Attach</span>
            </button>

            <input type="file" ref={fileInputRef} onChange={handleFileAttached} accept="*" multiple className="hidden" />
          </div>

          <div className="flex items-center justify-end gap-3">
            <div className={`hidden text-right text-[10px] font-mono uppercase tracking-wider xl:block ${textMutedClass}`}>
              <div>
                <span className="font-black">Preset:</span> {presetLabel(aiPreset)}
              </div>
              <div>
                {accuracyMode} · {outputLengthLabel(responseLength)} · {jobSensitivity}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className={`inline-flex h-10 min-w-[112px] items-center justify-center gap-2 rounded-xl border px-4 text-[12px] font-black uppercase tracking-wide transition-all ${primaryButtonClass}`}
              id="chat-submit-btn"
            >
              <Send className="h-4 w-4" />
              <span>Send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolToggle({
  label,
  active,
  onClick,
  icon,
  activeText,
  activeClass,
  inactiveClass,
  baseClass,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  activeText: string;
  activeClass: string;
  inactiveClass: string;
  baseClass: string;
  title?: string;
}) {
  return (
    <button type="button" onClick={onClick} className={`${baseClass} ${active ? activeClass : inactiveClass}`} title={title}>
      {icon}
      <span>{label}</span>
      <span
        className={`rounded-md border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide ${
          active
            ? "border-emerald-300/25 bg-emerald-300/10 text-emerald-100 opacity-95"
            : "border-white/10 bg-black/10 text-current opacity-55"
        }`}
      >
        {activeText}
      </span>
    </button>
  );
}

function StatusLine({
  children,
  icon,
  tone,
  isLight,
  onDismiss,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  tone: "success" | "warning" | "danger";
  isLight: boolean;
  onDismiss?: () => void;
}) {
  const toneClass =
    tone === "success"
      ? isLight
        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
        : "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
      : tone === "danger"
        ? isLight
          ? "border-rose-200 bg-rose-50 text-rose-800"
          : "border-rose-500/25 bg-rose-500/10 text-rose-300"
        : isLight
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-amber-500/25 bg-amber-500/10 text-amber-300";

  return (
    <div className={`mt-2 flex items-start gap-2 rounded-xl border px-3 py-2 text-[11px] leading-relaxed ${toneClass}`}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">{children}</div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="shrink-0 rounded-md p-0.5 opacity-60 hover:opacity-100">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function SegmentedControl({
  label,
  value,
  onChange,
  options,
  isLight,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  isLight: boolean;
}) {
  return (
    <div>
      <div className={`mb-1.5 text-[10px] font-black uppercase tracking-wider ${isLight ? "text-slate-500" : "text-neutral-500"}`}>{label}</div>
      <div
        className={`grid gap-1 rounded-xl border p-1 ${isLight ? "border-slate-200 bg-slate-50" : "border-white/10 bg-black/10"}`}
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <button
              type="button"
              key={option.value}
              onClick={() => onChange(option.value)}
              className={`rounded-lg px-2 py-1.5 text-[11px] font-extrabold transition-all ${
                selected
                  ? isLight
                    ? "bg-[#143F8F] text-white"
                    : "bg-[#F6A400] text-[#071A3D]"
                  : isLight
                    ? "text-slate-500 hover:bg-white hover:text-slate-900"
                    : "text-neutral-500 hover:bg-white/10 hover:text-neutral-200"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SelectControl({
  label,
  value,
  onChange,
  options,
  isLight,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  isLight: boolean;
}) {
  return (
    <label className="block">
      <span className={`mb-1.5 block text-[10px] font-black uppercase tracking-wider ${isLight ? "text-slate-500" : "text-neutral-500"}`}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-9 w-full rounded-xl border px-3 text-[12px] font-semibold outline-none ${
          isLight
            ? "border-slate-200 bg-slate-50 text-slate-900 focus:border-[#143F8F]"
            : "border-white/10 bg-black/20 text-neutral-100 focus:border-[#F6A400]"
        }`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
